/**
 * FREE SAMPLES PAGE - Block-Based Version (CRO v2)
 * Developer: Kaspian (Kaspian CRO)
 * Last Updated: February 2026
 * 
 * Architecture:
 * - Categories (Step 1): Server-rendered in Liquid with shopify_attributes
 * - Products (Step 2): Server-rendered in Liquid, grouped by category — JS shows/hides
 * - Swatches (Step 3): AJAX-loaded from collection JSON endpoint
 * 
 * Features:
 * - 3-step selection flow (Category → Product → Swatches)
 * - Cross-product selection persistence (selections survive product switches)
 * - Redesigned cart bar with progress tracking
 * - Sticky cart bar on mobile during swatch selection
 * - Count pulse animation + smart CTA text
 * - AJAX add-to-cart with cart validation
 * - Response caching + background prefetching
 * - Max sample enforcement with toast notifications
 * - Theme Editor click-to-select on category & product cards
 */

(function($) {
  'use strict';

  // Only run on free samples page
  if (!$('#step-1').length || typeof window.freeSamplesData === 'undefined') return;

  // ── Selection state ──
  let selectedSwatches = {};
  let currentProductHandle = null;

  // Helpers
  function getAllSelectedIds() {
    var ids = [];
    Object.keys(selectedSwatches).forEach(function(handle) {
      ids = ids.concat(selectedSwatches[handle]);
    });
    return ids;
  }

  function getTotalCount() {
    var count = 0;
    Object.keys(selectedSwatches).forEach(function(handle) {
      count += selectedSwatches[handle].length;
    });
    return count;
  }

  function getProductCount(handle) {
    return (selectedSwatches[handle] || []).length;
  }

  function resetAllSelections() {
    selectedSwatches = {};
    currentProductHandle = null;
  }

  const swatchesList = $('.swatches-list');
  const sampleCount = $('.sample-count');
  const maxSamples = window.maxFreeSamples || 10;
  const maxMessage = window.maxFreeSamplesMessage || 'Maximum ' + maxSamples + ' free samples per customer.';

  // ── Cart bar elements ──
  const cartBar = $('.fs-cart-bar');
  const cartBarInner = $('.fs-cart-bar__inner');
  const cartBarSuccess = $('.fs-cart-bar__success');
  const successText = $('.fs-cart-bar__success-text');
  const countLabel = $('.sample-count-label');
  const progressFill = $('.fs-cart-bar__progress-fill');
  const progressBar = $('.fs-cart-bar__progress');
  const cartBtn = $('.fs-cart-bar__btn');
  const btnText = $('.fs-cart-bar__btn-text');

  // =========================================================================
  // CART BAR: Reset success state
  // =========================================================================
  function resetSuccessState() {
    cartBarSuccess.hide();
    cartBarInner.show();
  }

  // =========================================================================
  // CART BAR: Update UI state
  // =========================================================================
  function updateCartBar(count) {
    resetSuccessState();
    var prevCount = parseInt(sampleCount.text()) || 0;

    sampleCount.text(count);

    if (count !== prevCount) {
      sampleCount.addClass('is-bumping');
      setTimeout(function() {
        sampleCount.removeClass('is-bumping');
      }, 250);
    }

    countLabel.text(count === 1 ? 'sample selected' : 'samples selected');

    var pct = Math.min((count / maxSamples) * 100, 100);
    progressFill.css('width', pct + '%');
    progressBar.attr('aria-valuenow', count);

    if (count >= maxSamples) {
      progressFill.addClass('is-full');
    } else {
      progressFill.removeClass('is-full');
    }

    if (count > 0) {
      cartBtn.prop('disabled', false).addClass('is-active');
      var plural = count === 1 ? 'Sample' : 'Samples';
      btnText.text('Order ' + count + ' Free ' + plural);
    } else {
      cartBtn.prop('disabled', true).removeClass('is-active');
      btnText.text('Select Samples Above');
    }

    updateProductBadges();
  }

  // =========================================================================
  // PRODUCT CARD BADGES — show "3 selected" on cards with picks
  // =========================================================================
  function updateProductBadges() {
    // Find the currently visible product group
    var visibleGroup = $('.product-list-group:visible');
    if (!visibleGroup.length) return;

    visibleGroup.find('.builder__control').each(function() {
      var handle = $(this).find('input').val();
      var count = getProductCount(handle);
      var badge = $(this).find('.fs-product-badge');

      if (count > 0) {
        var text = count + ' selected';
        if (badge.length) {
          badge.text(text);
        } else {
          $(this).find('.builder__control-label').after(
            '<span class="fs-product-badge">' + text + '</span>'
          );
        }
        $(this).find('.fs-swatch-count').hide();
      } else {
        badge.remove();
        $(this).find('.fs-swatch-count').show();
      }
    });
  }

  // =========================================================================
  // STICKY CART BAR — Always fixed when visible (desktop + mobile)
  // Moves bar to <body> to escape parent transforms that break position:fixed
  // =========================================================================
  var stickyActive = false;
  var cartBarPlaceholder = $('<div class="fs-cart-bar-placeholder" style="display:none;"></div>');

  function activateSticky() {
    if (stickyActive) return;
    // Insert placeholder where bar originally lives
    cartBar.before(cartBarPlaceholder);
    // Move bar to body — escapes any parent with transform/filter/will-change
    $('body').append(cartBar);
    cartBar.addClass('is-sticky');
    document.body.classList.add('fs-sticky-active');
    stickyActive = true;
  }

  function removeSticky() {
    if (!stickyActive) return;
    cartBar.removeClass('is-sticky');
    document.body.classList.remove('fs-sticky-active');
    // Move bar back to its original position in the DOM
    cartBarPlaceholder.after(cartBar);
    cartBarPlaceholder.detach();
    stickyActive = false;
  }

  // Aliases so existing calls don't break
  function initSticky() { activateSticky(); }
  function startObserving() {}
  function stopObserving() {}

  // =========================================================================
  // PERFORMANCE: AJAX cache + prefetch
  // =========================================================================
  const swatchCache = {};

  function fetchSwatches(collectionHandle) {
    if (swatchCache[collectionHandle]) {
      return $.Deferred().resolve(swatchCache[collectionHandle]).promise();
    }

    return $.ajax({
      url: '/collections/' + collectionHandle + '?view=free-samples-json'
    }).then(function(result) {
      var json;
      try {
        json = typeof result === 'string' ? JSON.parse(result.trim()) : result;
      } catch(e) {
        console.error('Free samples JSON parse error:', e);
        return null;
      }
      swatchCache[collectionHandle] = json;
      return json;
    });
  }

  function prefetchCategory(categoryId) {
    var categoryData = window.freeSamplesData[categoryId];
    if (!categoryData || !categoryData.products) return;
    categoryData.products.forEach(function(product) {
      if (!swatchCache[product.swatchCollection]) {
        fetchSwatches(product.swatchCollection);
      }
    });
  }

  // =========================================================================
  // TOAST NOTIFICATION
  // =========================================================================
  function showToast(message, type) {
    $('.fs-toast').remove();
    var icon = type === 'success' ? '✓' : '⚠';
    var toast = $('<div class="fs-toast fs-toast--' + type + '">' +
      '<span class="fs-toast__icon">' + icon + '</span>' +
      '<span class="fs-toast__message">' + message + '</span></div>');
    $('body').append(toast);
    setTimeout(function() { toast.addClass('fs-toast--visible'); }, 10);
    setTimeout(function() {
      toast.removeClass('fs-toast--visible');
      setTimeout(function() { toast.remove(); }, 300);
    }, 3000);
  }

  // =========================================================================
  // BUILD SWATCH HTML
  // =========================================================================
  function buildSwatchMarkup(json, collectionHandle) {
    if (!json || json.result_count === 0) {
      return '<li><p class="text-center">No swatches found in this collection.</p></li>';
    }

    var productSelections = selectedSwatches[collectionHandle] || [];

    var markup = '';
    $.each(json.results, function(i, swatch) {
      var isSelected = productSelections.indexOf(swatch.id) > -1;
      var isOutOfStock = swatch.available === false;
      var thumbImage = swatch.image ? swatch.image.replace(/535x344|x250|x500/g, 'x300') : '';

      markup += '<li>' +
        '<div class="builder__item' + (isSelected ? ' is-selected' : '') + (isOutOfStock ? ' is-out-of-stock' : '') + '" data-variant-id="' + swatch.id + '">' +
          '<label class="control builder__control swatch-clickable">' +
            '<input type="checkbox" value="' + swatch.id + '"' + (isSelected ? ' checked' : '') + (isOutOfStock ? ' disabled' : '') + '>' +
            '<span class="builder__control-indicator"></span>' +
            '<span class="builder__control-item">' +
              '<img class="thumb lazyload" src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" data-src="' + thumbImage + '" alt="' + (swatch.name || '') + '" style="width:100%;">' +
              '<span class="fs-zoom-icon" data-zoom-src="' + thumbImage.replace('x300', 'x600') + '" data-zoom-name="' + (swatch.name || '') + '"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg></span>' +
            '</span>' +
          '</label>' +
          (isOutOfStock ? 
            '<span class="out-of-stock-badge">Out of Stock</span>' : 
            '<button type="button" class="sample-in-cart-btn">Sample in Cart <span class="remove-x">✕</span></button>') +
          '<span class="swatch-name">' + (swatch.name || '') + '</span>' +
        '</div>' +
      '</li>';
    });
    return markup;
  }

  // =========================================================================
  // BACK NAVIGATION
  // =========================================================================
  $(document).on('click', '.fs-back-link', function(e) {
    e.preventDefault();
    var target = $(this).data('back');

    if (target === 'categories') {
      resetAllSelections();
      updateCartBar(0);
      swatchesList.empty();
      // Hide all product groups
      $('.product-list-group').hide();
      $('#step-2').hide();
      $('#step-3').hide();
      cartBar.hide();
      stopObserving();
      removeSticky();

      $('.product-groups input[type="radio"]').prop('checked', false);
      $('.product-groups .builder__panel').removeClass('is-active');

      // Reset any product radio states
      $('.product-list-group input[type="radio"]').prop('checked', false);
      $('.product-list-group .builder__panel').removeClass('is-active');
      // Clean up product badges
      $('.product-list-group .fs-product-badge').remove();
      $('.product-list-group .fs-swatch-count').show();

      $('html, body').animate({
        scrollTop: $('#step-1').offset().top - ($('header.site-header').outerHeight() || 0) - 10
      }, 500);

    } else if (target === 'products') {
      currentProductHandle = null;
      swatchesList.empty();
      $('#step-3').hide();

      // Reset Step 3 heading back to default
      $('.step-3-heading').text('Choose Color');

      var total = getTotalCount();
      if (total > 0) {
        cartBar.show();
        updateCartBar(total);
      } else {
        cartBar.hide();
        stopObserving();
        removeSticky();
      }

      // Uncheck product radio (visual only — selections persist)
      $('.product-list-group:visible input[type="radio"]').prop('checked', false);
      $('.product-list-group:visible .builder__panel').removeClass('is-active');

      $('html, body').animate({
        scrollTop: $('#step-2').offset().top - ($('header.site-header').outerHeight() || 0) - 10
      }, 500);
    }
  });

  // =========================================================================
  // STEP 1: Category Selection
  // =========================================================================
  $(document).on('click', '.product-groups .builder__control', function() {
    var categoryId = $(this).find('input').val();

    // Reset selections when switching category
    resetAllSelections();
    updateCartBar(0);
    swatchesList.empty();
    $('#step-3').hide();
    cartBar.hide();
    stopObserving();
    removeSticky();

    // Hide all product groups, show the selected one
    $('.product-list-group').hide();
    var targetGroup = $('.product-list-group[data-category-id="' + categoryId + '"]');

    if (targetGroup.length && targetGroup.find('.builder__panel').length) {
      targetGroup.show();
      $('#step-2').show();

      // Reset product states in this group
      targetGroup.find('input[type="radio"]').prop('checked', false);
      targetGroup.find('.builder__panel').removeClass('is-active');
      targetGroup.find('.fs-product-badge').remove();
      targetGroup.find('.fs-swatch-count').show();

      // Prefetch swatches for all products in this category
      prefetchCategory(categoryId);

      $('html, body').animate({
        scrollTop: $('#step-2').offset().top - $('header.site-header').outerHeight()
      }, 600);
    } else {
      // No products configured
      $('.product-list-group').hide();
      $('#step-2').show();
      // Show a message inside the container
      var container = $('.product-list-container');
      // Check if there's already a no-products message
      if (!container.find('.fs-no-products-msg').length) {
        container.append('<p class="fs-no-products-msg text-center" style="padding:20px;color:#666;">No products configured for this category. Add Product blocks in Theme Editor.</p>');
      }
      container.find('.fs-no-products-msg').show();

      $('html, body').animate({
        scrollTop: $('#step-2').offset().top - $('header.site-header').outerHeight()
      }, 600);
    }
  });

  // =========================================================================
  // STEP 2: Product Selection — now reads from pre-rendered Liquid cards
  // =========================================================================
  $(document).on('click', '.product-list-group .builder__control', function() {
    var swatchCollectionHandle = $(this).find('input').val();
    var swatchLabel = $(this).find('input').data('swatch-label');

    // Track current product
    currentProductHandle = swatchCollectionHandle;

    // Update Step 3 heading if custom label is set
    if (swatchLabel && swatchLabel.trim() !== '') {
      $('.step-3-heading').text(swatchLabel);
    } else {
      $('.step-3-heading').text('Choose Color');
    }

    $('#step-3').show();
    cartBar.show();
    initSticky();

    // Render swatches
    if (swatchCache[swatchCollectionHandle]) {
      swatchesList.html(buildSwatchMarkup(swatchCache[swatchCollectionHandle], swatchCollectionHandle));
    } else {
      swatchesList.html('<li><div class="loading-animation"></div></li>');
    }

    fetchSwatches(swatchCollectionHandle)
      .done(function(json) {
        swatchesList.html(buildSwatchMarkup(json, swatchCollectionHandle));
      })
      .fail(function() {
        swatchesList.html('<li><p class="text-center" style="color:red;">Error loading swatches. Check collection handle.</p></li>');
      });

    updateCartBar(getTotalCount());

    $('html, body').animate({
      scrollTop: $('#step-3').offset().top - $('header.site-header').outerHeight()
    }, 600);

    setTimeout(function() { startObserving(); }, 700);
  });

  // =========================================================================
  // STEP 3: Swatch Selection (click on image)
  // =========================================================================
  function toggleSwatch(item, input) {
    if (item.hasClass('is-out-of-stock')) return;
    if (!currentProductHandle) return;

    var variantId = parseInt(input.val());

    if (!selectedSwatches[currentProductHandle]) {
      selectedSwatches[currentProductHandle] = [];
    }

    var productArr = selectedSwatches[currentProductHandle];

    if (input.is(':checked')) {
      input.prop('checked', false);
      item.removeClass('is-selected');
      selectedSwatches[currentProductHandle] = productArr.filter(function(id) { return id !== variantId; });
      if (selectedSwatches[currentProductHandle].length === 0) {
        delete selectedSwatches[currentProductHandle];
      }
    } else {
      if (getTotalCount() >= maxSamples) {
        showToast(maxMessage, 'warning');
        return;
      }
      input.prop('checked', true);
      item.addClass('is-selected');
      productArr.push(variantId);
    }

    updateCartBar(getTotalCount());
  }

  $(document).on('click', '.swatches-list .swatch-clickable', function(e) {
    e.preventDefault();
    var item = $(this).closest('.builder__item');
    var input = $(this).find('input');
    toggleSwatch(item, input);
  });

  $(document).on('keydown', '.swatches-list input[type="checkbox"]', function(e) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      var item = $(this).closest('.builder__item');
      toggleSwatch(item, $(this));
    }
  });

  $(document).on('click', '.sample-in-cart-btn', function(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentProductHandle) return;

    var item = $(this).closest('.builder__item');
    var variantId = parseInt(item.data('variant-id'));
    var input = item.find('input[type="checkbox"]');
    
    input.prop('checked', false);
    item.removeClass('is-selected');

    if (selectedSwatches[currentProductHandle]) {
      selectedSwatches[currentProductHandle] = selectedSwatches[currentProductHandle].filter(function(id) { return id !== variantId; });
      if (selectedSwatches[currentProductHandle].length === 0) {
        delete selectedSwatches[currentProductHandle];
      }
    }
    
    updateCartBar(getTotalCount());
  });

  // =========================================================================
  // ADD TO CART — with validation
  // =========================================================================
  cartBar.on('click', '.add-free-samples-new', async function() {
    var allIds = getAllSelectedIds();

    if (allIds.length === 0) {
      showToast('Please select at least one swatch.', 'warning');
      return;
    }

    var btn = $(this);
    btn.addClass('is-loading');
    btnText.text('Adding…');

    // Fetch current cart to filter out duplicates
    var cartData = await $.getJSON('/cart.js');
    var existingIds = cartData.items.map(function(item) { return item.variant_id; });
    var newIds = allIds.filter(function(id) { return existingIds.indexOf(id) === -1; });

    if (newIds.length === 0) {
      showToast('These swatches are already in your cart.', 'warning');
      btn.removeClass('is-loading');
      updateCartBar(getTotalCount());
      return;
    }

    if (newIds.length < allIds.length) {
      var skipped = allIds.length - newIds.length;
      showToast(skipped + ' swatch' + (skipped > 1 ? 'es' : '') + ' already in cart, adding the rest.', 'info');
    }

    var items = newIds.map(function(id) {
      return {
        id: id,
        quantity: 1,
        vendor: 'FDB Sample'
      };
    });

    var isValid = true;
    if (typeof validateAddFreeSamples === 'function') {
      isValid = await validateAddFreeSamples(items);
    }

    if (!isValid) {
      btn.removeClass('is-loading');
      updateCartBar(getTotalCount());
      return;
    }

    $.ajax({
      url: '/cart/add.js',
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({ items: items }),
      success: function() {
        var addedCount = newIds.length;
        var plural = addedCount === 1 ? 'sample' : 'samples';
        
        $.getJSON('/cart.js', function(cart) {
          $('.cart-count, .cart__count, [data-cart-count]').text(cart.item_count);
        });

        resetAllSelections();
        swatchesList.find('input:checked').prop('checked', false);
        swatchesList.find('.is-selected').removeClass('is-selected');
        
        btn.removeClass('is-loading');

        successText.text(addedCount + ' ' + plural + ' added to cart!');
        cartBarInner.hide();
        cartBarSuccess.show();
      },
      error: function() {
        showToast('Error adding to cart. Please try again.', 'warning');
        btn.removeClass('is-loading');
        updateCartBar(getTotalCount());
      }
    });
  });

  // =========================================================================
  // BROWSE MORE — reset to Step 1 from success state
  // =========================================================================
  cartBar.on('click', '[data-action="browse-more"]', function() {
    cartBarSuccess.hide();
    cartBarInner.show();
    resetAllSelections();
    updateCartBar(0);

    swatchesList.empty();
    $('.product-list-group').hide();
    $('#step-2').hide();
    $('#step-3').hide();
    cartBar.hide();
    stopObserving();
    removeSticky();
    $('.step-3-heading').text('Choose Color');

    $('.product-groups input[type="radio"]').prop('checked', false);
    $('.product-groups .builder__panel').removeClass('is-active');

    // Clean up product states
    $('.product-list-group input[type="radio"]').prop('checked', false);
    $('.product-list-group .builder__panel').removeClass('is-active');
    $('.product-list-group .fs-product-badge').remove();
    $('.product-list-group .fs-swatch-count').show();

    $('html, body').animate({
      scrollTop: $('#step-1').offset().top - ($('header.site-header').outerHeight() || 0) - 10
    }, 500);
  });

})(jQuery);